import { OntologyValueType } from 'features/dynamicOntology'
import { createPatchData, useRolodexEdit } from 'features/fde'
import {
  normalizeDocumentCategories,
  RolodexDocumentEntry,
} from 'features/files'
import isEqual from 'lodash/isEqual'
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  DocumentSectionFields,
  useDocumentSectionFields,
  useSaveDocumentNotes,
  useSaveDocumentSectionFields,
} from './fieldEditor/documentSectionFieldsApi'
import { useExistingDocumentCategories } from './useDocumentCategories'

export interface DocumentDataState {
  miscFields: OntologyValueType
  setMiscFields: Dispatch<SetStateAction<OntologyValueType | undefined>>
  notes: string
  setNotes: Dispatch<SetStateAction<string | undefined>>
  categories: ReadonlyArray<string>
  setCategories: Dispatch<SetStateAction<ReadonlyArray<string>>>
  hasChanges: boolean
  isLoading: boolean
  isSaving: boolean
  existingCategories: ReadonlyArray<string>
  save: () => Promise<void>
  reset: () => void
}

export interface DocumentSectionDataState {
  documentSectionFields: ReadonlyMap<string, DocumentSectionFields>
  localDocumentSectionFields:
    | ReadonlyMap<string, DocumentSectionFields>
    | undefined
  setLocalDocumentSectionFields: Dispatch<
    SetStateAction<ReadonlyMap<string, DocumentSectionFields> | undefined>
  >
  deleteDocumentFieldIds: ReadonlyArray<string>
  setDeleteDocumentFieldIds: Dispatch<SetStateAction<ReadonlyArray<string>>>
  hasChanges: boolean
  isLoading: boolean
  isSaving: boolean
  save: () => Promise<void>
  reset: () => void
  forceRefresh: () => Promise<void>
}

export interface DocumentEditContextValue {
  documentData: DocumentDataState
  sectionData: DocumentSectionDataState
  isLoading: boolean
  isSaving: boolean
  doSave: () => Promise<void>
  hasChanges: boolean
}

export const useDocumentData = (
  document: RolodexDocumentEntry,
  externalIsLoading = false
): DocumentDataState => {
  const [localMiscFields, setLocalMiscFields] = useState<
    OntologyValueType | undefined
  >(undefined)
  const [localNotes, setLocalNotes] = useState<string | undefined>(undefined)
  const [localCategories, setLocalCategories] = useState<
    ReadonlyArray<string> | undefined
  >(undefined)
  const [isSaving, setIsSaving] = useState(false)

  const originalMiscFields = useMemo(
    () => (document.fields as OntologyValueType) ?? ({} as OntologyValueType),
    [document.fields]
  )
  const originalNotes = useMemo(() => document.notes ?? '', [document.notes])
  const originalCategories = useMemo(
    () => normalizeDocumentCategories(document.categories),
    [document.categories]
  )

  useEffect(() => {
    setLocalMiscFields(undefined)
  }, [document.entry_id, originalMiscFields])

  useEffect(() => {
    setLocalNotes(undefined)
  }, [document.entry_id, originalNotes])

  useEffect(() => {
    setLocalCategories(undefined)
  }, [document.entry_id, originalCategories])

  const setMiscFields = useCallback(
    (value: SetStateAction<OntologyValueType | undefined>) => {
      setLocalMiscFields((prev) => {
        const baseValue = prev ?? originalMiscFields
        const nextValue =
          typeof value === 'function'
            ? (
                value as (
                  prevState: OntologyValueType | undefined
                ) => OntologyValueType | undefined
              )(baseValue)
            : value

        if (nextValue === undefined) {
          return undefined
        }

        const normalizedNext = nextValue as OntologyValueType
        return isEqual(normalizedNext, originalMiscFields)
          ? undefined
          : normalizedNext
      })
    },
    [originalMiscFields]
  )

  const setNotes = useCallback(
    (value: SetStateAction<string | undefined>) => {
      setLocalNotes((prev) => {
        const baseValue = prev ?? originalNotes
        const nextValue =
          typeof value === 'function'
            ? (value as (prevState: string) => string | undefined)(baseValue)
            : value

        if (nextValue === undefined) {
          return undefined
        }

        return nextValue === originalNotes ? undefined : nextValue
      })
    },
    [originalNotes]
  )

  const setCategories = useCallback(
    (value: SetStateAction<ReadonlyArray<string>>) => {
      setLocalCategories((prev) => {
        const baseValue = prev ?? originalCategories
        const nextValue =
          typeof value === 'function'
            ? (
                value as (
                  prevState: ReadonlyArray<string>
                ) => ReadonlyArray<string>
              )(baseValue)
            : value

        const normalizedNext = normalizeDocumentCategories(nextValue)

        return isEqual(normalizedNext, originalCategories)
          ? undefined
          : normalizedNext
      })
    },
    [originalCategories]
  )

  const miscFields = useMemo(
    () => (localMiscFields ?? originalMiscFields) as OntologyValueType,
    [localMiscFields, originalMiscFields]
  )

  const notes = localNotes ?? originalNotes
  const categories = localCategories ?? originalCategories

  const hasChanges = useMemo(() => {
    if (
      localMiscFields !== undefined &&
      !isEqual(localMiscFields, originalMiscFields)
    ) {
      return true
    }
    if (
      localCategories !== undefined &&
      !isEqual(localCategories, originalCategories)
    ) {
      return true
    }
    return localNotes !== undefined
  }, [
    localMiscFields,
    localNotes,
    originalMiscFields,
    localCategories,
    originalCategories,
  ])

  const { rolodexPatch } = useRolodexEdit()
  const { doSave: saveDocumentNotes } = useSaveDocumentNotes()

  const {
    categories: existingCategories,
    isLoading: categoriesLoading,
    forceRefresh: categoriesForceRefresh,
  } = useExistingDocumentCategories(
    document.tags.organization
      ? { organization: document.tags.organization }
      : undefined
  )

  const saveDocumentData = useCallback(async () => {
    if (localMiscFields === undefined && localCategories === undefined) {
      return
    }

    const nextFields =
      localMiscFields === undefined ? document.fields : localMiscFields
    const nextCategories =
      localCategories === undefined
        ? originalCategories
        : normalizeDocumentCategories(localCategories)

    const patch = createPatchData(
      document.entry_id,
      {
        fields: document.fields,
        categories: originalCategories,
      },
      {
        fields: nextFields,
        categories: nextCategories,
      }
    )

    if (patch) {
      await rolodexPatch([patch])
      if (patch.data?.categories) {
        await categoriesForceRefresh()
      }
    }
  }, [
    localMiscFields,
    localCategories,
    document.fields,
    document.entry_id,
    originalCategories,
    rolodexPatch,
    categoriesForceRefresh,
  ])

  const save = useCallback(async () => {
    if (!hasChanges) {
      return
    }

    setIsSaving(true)
    try {
      if (localMiscFields !== undefined || localCategories !== undefined) {
        await saveDocumentData()
      }
      if (localNotes !== undefined) {
        await saveDocumentNotes(
          document.entry_id,
          localNotes === '' ? undefined : localNotes
        )
      }
      setLocalMiscFields(undefined)
      setLocalNotes(undefined)
      setLocalCategories(undefined)
    } finally {
      setIsSaving(false)
    }
  }, [
    document.entry_id,
    hasChanges,
    localMiscFields,
    localCategories,
    localNotes,
    saveDocumentNotes,
    saveDocumentData,
  ])

  const reset = useCallback(() => {
    setLocalMiscFields(undefined)
    setLocalNotes(undefined)
    setLocalCategories(undefined)
  }, [])

  return useMemo<DocumentDataState>(
    () => ({
      miscFields,
      setMiscFields,
      notes,
      setNotes,
      categories,
      setCategories,
      hasChanges,
      isLoading: externalIsLoading || categoriesLoading,
      isSaving,
      save,
      reset,
      existingCategories,
    }),
    [
      miscFields,
      setMiscFields,
      notes,
      setNotes,
      categories,
      setCategories,
      hasChanges,
      externalIsLoading,
      categoriesLoading,
      isSaving,
      save,
      reset,
      existingCategories,
    ]
  )
}

export const useDocumentSectionData = (
  document: RolodexDocumentEntry
): DocumentSectionDataState => {
  const sectionTags = useMemo(
    () => ({ [document.type]: document.entry_id }),
    [document.entry_id, document.type]
  )

  const { documentSectionFields, isLoading, forceRefresh } =
    useDocumentSectionFields(sectionTags)

  const [localDocumentSectionFields, setLocalDocumentSectionFields] = useState<
    ReadonlyMap<string, DocumentSectionFields> | undefined
  >(undefined)
  const [deleteDocumentFieldIds, setDeleteDocumentFieldIds] = useState<
    ReadonlyArray<string>
  >([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setLocalDocumentSectionFields(undefined)
    setDeleteDocumentFieldIds([])
  }, [document.entry_id])

  const hasChanges = useMemo(
    () => !!localDocumentSectionFields || deleteDocumentFieldIds.length > 0,
    [localDocumentSectionFields, deleteDocumentFieldIds]
  )

  const { rolodexDelete } = useRolodexEdit()
  const { doSave } = useSaveDocumentSectionFields()

  const save = useCallback(async () => {
    if (!hasChanges) {
      return
    }
    setIsSaving(true)
    try {
      if (localDocumentSectionFields?.size) {
        await doSave(Array.from(localDocumentSectionFields.values()))
      }
      await Promise.all(
        deleteDocumentFieldIds
          .filter((id) => documentSectionFields.has(id))
          .map((id) => rolodexDelete(id, true))
      )
      setLocalDocumentSectionFields(undefined)
      setDeleteDocumentFieldIds([])
      await forceRefresh()
    } finally {
      setIsSaving(false)
    }
  }, [
    deleteDocumentFieldIds,
    documentSectionFields,
    doSave,
    forceRefresh,
    hasChanges,
    localDocumentSectionFields,
    rolodexDelete,
  ])

  const reset = useCallback(() => {
    setLocalDocumentSectionFields(undefined)
    setDeleteDocumentFieldIds([])
  }, [])

  return useMemo<DocumentSectionDataState>(
    () => ({
      documentSectionFields,
      localDocumentSectionFields,
      setLocalDocumentSectionFields,
      deleteDocumentFieldIds,
      setDeleteDocumentFieldIds,
      hasChanges,
      isLoading,
      isSaving,
      save,
      reset,
      forceRefresh,
    }),
    [
      documentSectionFields,
      localDocumentSectionFields,
      setLocalDocumentSectionFields,
      deleteDocumentFieldIds,
      setDeleteDocumentFieldIds,
      hasChanges,
      isLoading,
      isSaving,
      save,
      reset,
      forceRefresh,
    ]
  )
}

const DocumentEditContext = createContext<DocumentEditContextValue | undefined>(
  undefined
)

export const useDocumentEditContext = (): DocumentEditContextValue => {
  const context = useContext(DocumentEditContext)
  if (!context) {
    throw new Error('useDocumentEditContext must be used within a provider')
  }
  return context
}

interface DocumentEditProviderProps {
  document: RolodexDocumentEntry
  isLoading?: boolean
  children: ReactNode
}

export const DocumentEditProvider: React.FC<DocumentEditProviderProps> = ({
  children,
  document,
  isLoading = false,
}) => {
  const documentData = useDocumentData(document, isLoading)
  const sectionData = useDocumentSectionData(document)

  const combinedLoading = documentData.isLoading || sectionData.isLoading
  const combinedSaving = documentData.isSaving || sectionData.isSaving
  const hasChanges = documentData.hasChanges || sectionData.hasChanges

  const doSave = useCallback(async () => {
    if (!hasChanges) {
      return
    }

    if (sectionData.hasChanges) {
      await sectionData.save()
    }
    if (documentData.hasChanges) {
      await documentData.save()
    }
  }, [documentData, sectionData, hasChanges])

  const contextValue = useMemo<DocumentEditContextValue>(
    () => ({
      documentData,
      sectionData,
      isLoading: combinedLoading,
      isSaving: combinedSaving,
      doSave,
      hasChanges,
    }),
    [
      documentData,
      sectionData,
      combinedLoading,
      combinedSaving,
      doSave,
      hasChanges,
    ]
  )

  return (
    <DocumentEditContext.Provider value={contextValue}>
      {children}
    </DocumentEditContext.Provider>
  )
}
